import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { trackingApi } from '@/api/resources';
import { TRACKING_POLL_MS } from './model';

export const trackingKeys = {
  availability: ['tracking', 'availability'] as const,
  overview: ['tracking', 'vehicles'] as const,
  /** The snapshot under the map's search; `''` is the whole fleet. */
  overviewFor: (search: string) => ['tracking', 'vehicles', { search }] as const,
  vehicle: (id: string) => ['tracking', 'vehicles', id] as const,
  units: ['tracking', 'units'] as const,
  /** One answered window. Never under `vehicles`, so nothing that refreshes the live map touches it. */
  history: (vehicleId: string, from: string, to: string) =>
    ['tracking', 'history', vehicleId, { from, to }] as const,
};

/**
 * Whether this deployment tracks vehicles at all. The **only** gate: every
 * other tracking endpoint exists only when it says yes. A failed call reads as
 * "not available", like the invoice reader's, so a feature that is off can
 * never break the screens it is not on. Asked once per session and kept.
 */
export function useTrackingAvailability(enabled = true) {
  const query = useQuery({
    queryKey: trackingKeys.availability,
    queryFn: trackingApi.availability,
    enabled,
    staleTime: Infinity,
    retry: false,
  });
  return {
    available: query.data?.available === true,
    settled: !query.isPending || !enabled,
    query,
  };
}

/**
 * The fleet's snapshot under the map's search, polled every
 * `TRACKING_POLL_MS` while the tab is visible and never in the background.
 * The search is the server's (`?search=`, C75): it narrows the vehicles and
 * the untracked list, and the URL's `q` is already debounced by
 * `SearchField`, so a keystroke is not a request. While a new search loads,
 * the previous answer stays up rather than the map blanking.
 *
 * `refreshFailed` is the case TanStack Query makes easy to miss: a poll that
 * fails keeps the **previous** answer on screen, with its ages frozen, looking
 * exactly as live as it did. The map draws itself stale when this is true.
 */
export function useTrackingOverview(enabled: boolean, search = '') {
  const term = search.trim();
  const query = useQuery({
    queryKey: trackingKeys.overviewFor(term),
    queryFn: () => trackingApi.vehicles(term || undefined),
    placeholderData: keepPreviousData,
    enabled,
    refetchInterval: TRACKING_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
  return { ...query, refreshFailed: query.isRefetchError };
}

/** One vehicle with its sensors, polled at the same pace while it is selected. */
export function useTrackedVehicle(vehicleId: string | null) {
  return useQuery({
    queryKey: trackingKeys.vehicle(vehicleId ?? ''),
    queryFn: () => trackingApi.vehicle(vehicleId as string),
    enabled: Boolean(vehicleId),
    refetchInterval: TRACKING_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}
